import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, FileText, Calendar, User, Filter, Image as ImageIcon } from 'lucide-react';

const MediaCenter = () => {
  const api = useApi();
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterType, setFilterType] = useState('all');

  // Form state for creating/editing media content
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Technology',
    media_type: 'blog',
    author: 'Blu Digital Technologies',
    read_time: '',
    image_url: '',
    is_published: true,
    publication_date: new Date().toISOString().split('T')[0]
  });

  const mediaTypes = [
    { value: 'blog', label: 'Blog Post', icon: '📝' },
    { value: 'press', label: 'Press Release', icon: '📰' },
    { value: 'news', label: 'News Article', icon: '📢' }
  ];

  const categoryOptions = [
    'Technology', 'Government', 'Enterprise', 'AI & Innovation', 
    'Web Development', 'Digital Transformation', 'Case Studies', 'Company News'
  ];

  useEffect(() => {
    loadMediaItems();
  }, []);

  const loadMediaItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/media');
      if (response.success) {
        setMediaItems(response.data);
      }
    } catch (error) {
      console.error('Error loading media items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async () => {
    try {
      const itemData = {
        ...formData,
        publication_date: new Date(formData.publication_date).toISOString()
      };

      const response = await api.post('/admin/media', itemData);
      if (response.success) {
        loadMediaItems();
        resetForm();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating media item:', error);
    }
  };

  const handleUpdateItem = async () => {
    try {
      const itemData = {
        ...formData,
        publication_date: new Date(formData.publication_date).toISOString()
      };

      const response = await api.put(`/admin/media/${editingItem}`, itemData);
      if (response.success) {
        loadMediaItems();
        resetForm();
        setEditingItem(null);
      }
    } catch (error) {
      console.error('Error updating media item:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this media item?')) {
      try {
        const response = await api.delete(`/admin/media/${itemId}`);
        if (response.success) {
          loadMediaItems();
        }
      } catch (error) {
        console.error('Error deleting media item:', error);
      }
    }
  };

  const handleTogglePublished = async (itemId) => {
    try {
      const response = await api.put(`/admin/media/${itemId}/toggle`);
      if (response.success) {
        loadMediaItems();
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  };

  const startEdit = (item) => {
    setFormData({
      title: item.title,
      excerpt: item.excerpt,
      content: item.content || '',
      category: item.category,
      media_type: item.media_type,
      author: item.author,
      read_time: item.read_time || '',
      image_url: item.image_url || '',
      is_published: item.is_published,
      publication_date: new Date(item.publication_date).toISOString().split('T')[0]
    });
    setEditingItem(item.id);
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Technology',
      media_type: 'blog',
      author: 'Blu Digital Technologies',
      read_time: '',
      image_url: '',
      is_published: true,
      publication_date: new Date().toISOString().split('T')[0]
    });
  };

  const filteredItems = filterType === 'all' 
    ? mediaItems 
    : mediaItems.filter(item => item.media_type === filterType);

  const MediaCard = ({ item }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          {item.image_url ? (
            <img 
              src={item.image_url} 
              alt={item.title}
              className="w-16 h-16 object-cover rounded-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-xl">{mediaTypes.find(t => t.value === item.media_type)?.icon}</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded uppercase font-medium">
                {item.media_type}
              </span>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {item.category}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <User size={14} />
                <span>{item.author}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar size={14} />
                <span>{new Date(item.publication_date).toLocaleDateString()}</span>
              </div>
              {item.read_time && (
                <span>{item.read_time}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleTogglePublished(item.id)}
            className={`p-2 rounded-lg ${item.is_published ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
            title={item.is_published ? 'Published - Click to unpublish' : 'Unpublished - Click to publish'}
          >
            {item.is_published ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button
            onClick={() => startEdit(item)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Edit media item"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteItem(item.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete media item"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <p className="text-gray-600 text-sm mb-4">{item.excerpt}</p>
      
      {item.content && (
        <div className="text-xs text-gray-500">
          Content: {item.content.length} characters
        </div>
      )}
    </div>
  );

  const MediaForm = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingItem ? 'Edit Media Content' : 'Create New Media Content'}
        </h3>
        <button
          onClick={() => {
            setEditingItem(null);
            setShowCreateForm(false);
            resetForm();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Article title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media Type</label>
            <select
              value={formData.media_type}
              onChange={(e) => setFormData(prev => ({...prev, media_type: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {mediaTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
          <textarea
            value={formData.excerpt}
            onChange={(e) => setFormData(prev => ({...prev, excerpt: e.target.value}))}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief excerpt or summary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData(prev => ({...prev, content: e.target.value}))}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Full article content"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categoryOptions.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData(prev => ({...prev, author: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Author name"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Publication Date</label>
            <input
              type="date"
              value={formData.publication_date}
              onChange={(e) => setFormData(prev => ({...prev, publication_date: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Read Time</label>
            <input
              type="text"
              value={formData.read_time}
              onChange={(e) => setFormData(prev => ({...prev, read_time: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="5 min read"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image URL</label>
            <div className="relative">
              <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({...prev, image_url: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Image URL"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_published"
            checked={formData.is_published}
            onChange={(e) => setFormData(prev => ({...prev, is_published: e.target.checked}))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
            Published (visible to public)
          </label>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={editingItem ? handleUpdateItem : handleCreateItem}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            <span>{editingItem ? 'Update Content' : 'Create Content'}</span>
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setShowCreateForm(false);
              resetForm();
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Media Center</h2>
          <p className="text-gray-600 mt-1">Manage blog posts, press releases, and news articles</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingItem(null);
            resetForm();
          }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Content</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            filterType === 'all' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Content
        </button>
        {mediaTypes.map(type => (
          <button
            key={type.value}
            onClick={() => setFilterType(type.value)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filterType === type.value 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {type.icon} {type.label}
          </button>
        ))}
      </div>

      {(showCreateForm || editingItem) && <MediaForm />}

      <div className="space-y-4">
        {filteredItems.map(item => (
          <MediaCard key={item.id} item={item} />
        ))}
      </div>

      {filteredItems.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {filterType === 'all' ? 'No media content found' : `No ${mediaTypes.find(t => t.value === filterType)?.label.toLowerCase()} found`}
          </p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Create your first {filterType === 'all' ? 'content' : mediaTypes.find(t => t.value === filterType)?.label.toLowerCase()}
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaCenter;